/**
 * What the project-scope migration (card 7.1) would do in ONE records bucket —
 * pure, so every rule is a unit test.
 *
 *   1. An unbound project that already `contains` collections is bound to the
 *      workspace holding most of them (ties: the earlier workspace in the
 *      list) — no duplicate project is created for that workspace.
 *   2. (personal bucket only) every workspace still unbound gets a project of
 *      its own with a DETERMINISTIC id, `apiws-<workspaceId>`, so two devices
 *      migrating the same synced bucket write the same record, not two.
 *   3. A project that already exists under that id is left alone, bound or
 *      not — the user may have unbound it on purpose.
 *
 * Nothing is moved or deleted; the plan only ever binds or creates.
 */
import { PROJECT_COLORS } from '../graph/projectColors';
import { bindingOf, bindingRefusal, workspaceIdsOf } from './projectBindingFields';

export const workspaceProjectId = (workspaceId) => `apiws-${workspaceId}`;

/** A stable palette colour per workspace (the same on every device). */
function colorFor(workspaceId) {
  let h = 0;
  for (const ch of String(workspaceId)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PROJECT_COLORS[h % PROJECT_COLORS.length];
}

/**
 * @param teamId               the bucket's team, or null for personal
 * @param projects / links     the bucket's records
 * @param workspaces           workspaceStore.workspaces
 * @param collectionWorkspace  Map<collectionId, workspaceId> (only what could be read)
 * @param unreadWorkspaceIds   workspaces whose collections could not be read: never
 *                             given a new project (one may already hold them)
 * @param createMissing        true in the personal bucket
 * @returns {{ bind: Array<{ project, workspace }>, create: object[] }}
 */
export function planScopeMigration({ teamId = null, projects = [], links = [], workspaces = [], collectionWorkspace = new Map(), unreadWorkspaceIds = new Set(), createMissing = false }) {
  const byId = new Map(workspaces.filter((w) => w?.id != null).map((w) => [String(w.id), w]));
  const order = [...byId.keys()];
  const bound = new Set(projects.flatMap((p) => workspaceIdsOf(p)));
  const bind = [];

  for (const project of projects) {
    if (workspaceIdsOf(project).length) continue;
    const node = `project:${project.id}`;
    const counts = new Map();
    for (const l of links) {
      if (l.from !== node || l.rel !== 'contains' || !String(l.to).startsWith('collection:')) continue;
      const ws = collectionWorkspace.get(String(l.to).slice('collection:'.length));
      if (ws != null && byId.has(String(ws))) counts.set(String(ws), (counts.get(String(ws)) || 0) + 1);
    }
    const best = [...counts.entries()]
      .filter(([ws]) => !bindingRefusal(teamId, byId.get(ws)))
      .sort((a, b) => b[1] - a[1] || order.indexOf(a[0]) - order.indexOf(b[0]))[0];
    if (!best) continue;
    bind.push({ project, workspace: byId.get(best[0]) });
    bound.add(best[0]);
  }

  const create = [];
  if (createMissing) {
    const existing = new Set(projects.map((p) => String(p.id)));
    for (const [id, ws] of byId) {
      if (bound.has(id) || unreadWorkspaceIds.has(id) || existing.has(workspaceProjectId(id))) continue;
      if (bindingRefusal(teamId, ws)) continue;
      create.push({
        id: workspaceProjectId(id),
        name: String(ws.name || '').trim() || 'Untitled project',
        description: '',
        color: colorFor(id),
        ...bindingOf(ws),
      });
      bound.add(id);
    }
  }
  return { bind, create };
}
